/* Feb-2026 · Token Cap Gate
 *
 * Full-screen overlay that fires when a user hits 100 % of their monthly
 * AI token cap. The editor content stays mounted but is covered so the
 * writer can't keep drafting. They can:
 *   a) Buy a top-up pack with one click → Stripe checkout.
 *   b) "Dismiss for now" — hides for 4 hours via localStorage timestamp.
 *
 * Rules:
 *  - Only for capped plan users (not BYOK, not unlimited).
 *  - pct must be >= 100 to show (>= 90 shows the smaller nudge banner).
 *  - Dismiss cooldown is 4 hours, not monthly — at 100 % we want to re-
 *    surface this every session until they buy or their month resets.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Zap, ShoppingBag, Loader2, X, PackageCheck, ArrowUpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usageApi } from '@/lib/api';
import { paymentsApi, subscriptionsApi } from '@/lib/auth_api';

// ── Upgrade paths ─────────────────────────────────────────────────────────
const UPGRADE_PATH = {
  author:    { next_id: 'publisher', name: 'Publisher', price: 119, tokens: '4.5 M' },
  publisher: { next_id: 'studio',   name: 'Studio',    price: 449, tokens: '20 M'  },
};

const CAP_PCT       = 100;
const DISMISS_HOURS = 4;
const DISMISS_KEY   = 'token_cap_gate_dismissed_until';

// ── Tier → preferred pack order ──────────────────────────────────────────
const TIER_LEAD = {
  author:    'starter',   // 50K for $10    — affordable, fits a chapter sprint
  publisher: 'pro',       // 250K for $40   — matches Publisher monthly throughput
  studio:    'studio',    // 1M for $120    — Studio-scale burst
};

function isDismissed() {
  try {
    const until = parseInt(window.localStorage.getItem(DISMISS_KEY) || '0', 10);
    return Date.now() < until;
  } catch { return false; }
}
function writeDismiss() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_HOURS * 3600_000));
  } catch { /* noop */ }
}

function fmtM(tokens) {
  if (!tokens) return '?';
  const m = tokens / 1_000_000;
  return m >= 1 ? `${m % 1 === 0 ? m : m.toFixed(1)} M` : `${Math.round(tokens / 1_000)} K`;
}

// ── Pack card ─────────────────────────────────────────────────────────────
function PackCard({ pack, buying, isLead, onBuy }) {
  const active = buying === pack.id;
  return (
    <button
      onClick={() => onBuy(pack.id)}
      disabled={!!buying}
      data-testid={`cap-gate-pack-${pack.id}`}
      className={[
        'relative flex w-full flex-col items-center rounded-xl border px-4 py-4 transition-all',
        isLead
          ? 'border-rose-400/70 bg-rose-500/10 ring-1 ring-rose-400/40 shadow-lg shadow-rose-500/10'
          : 'border-white/10 bg-white/5 hover:border-white/20',
        active ? 'opacity-60 cursor-wait' : 'cursor-pointer hover:scale-[1.02]',
      ].join(' ')}
    >
      {isLead && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-rose-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white shadow">
          recommended
        </span>
      )}
      <span className="mt-1 text-2xl font-bold text-white">{fmtM(pack.tokens)}</span>
      <span className="text-[11px] uppercase tracking-wide text-white/50">tokens</span>
      <span className="mt-2 text-lg font-semibold text-white">${pack.price}</span>
      <span className="mt-0.5 text-[10.5px] text-white/40">one-time · never expires</span>
      <div className={[
        'mt-3 w-full rounded-lg py-1.5 text-center text-[12px] font-semibold transition-colors',
        isLead ? 'bg-rose-500 text-white hover:bg-rose-400'
               : 'bg-white/10 text-white hover:bg-white/20',
      ].join(' ')}>
        {active ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Buy & continue writing'}
      </div>
    </button>
  );
}

// ── Main gate ─────────────────────────────────────────────────────────────
export default function TokenCapGate() {
  const [usage,     setUsage]     = useState(null);
  const [packs,     setPacks]     = useState(null);
  const [buying,    setBuying]    = useState(null);
  const [dismissed, setDismissed] = useState(isDismissed);

  // fetch usage
  useEffect(() => {
    if (dismissed) return;
    usageApi.me().then(setUsage).catch(() => null);
  }, [dismissed]);

  // checkout handler — declared before early returns
  const handleBuy = useCallback(async (packId) => {
    setBuying(packId);
    try {
      const result = await paymentsApi.checkoutPackage(packId, window.location.href);
      const url = result?.checkout_url || result?.url;
      if (url) window.location.href = url;
    } catch {
      setBuying(null);
    }
  }, []);

  // subscription upgrade handler
  const handleUpgrade = useCallback(async (planId) => {
    setBuying(`upgrade_${planId}`);
    try {
      const result = await subscriptionsApi.checkout(planId, 'monthly', window.location.href);
      const url = result?.checkout_url || result?.url;
      if (url) window.location.href = url;
    } catch {
      setBuying(null);
    }
  }, []);

  // fetch packs lazily once usage is known and cap is hit
  useEffect(() => {
    if (!usage || dismissed) return;
    const { is_byok = false, llm_cap_cents = -1, llm_spent_cents = 0,
            tokens_monthly = 0, llm_tokens_this_month = 0 } = usage;
    if (is_byok || llm_cap_cents <= 0) return;
    const pct = tokens_monthly > 0
      ? Math.min(100, Math.round((llm_tokens_this_month / tokens_monthly) * 100))
      : Math.min(100, Math.round((llm_spent_cents / llm_cap_cents) * 100));
    if (pct < CAP_PCT) return;
    paymentsApi.packages()
      .then(data => {
        const raw = Array.isArray(data) ? data : (data?.packages ?? data?.pricing ?? []);
        // Sort: lead pack first, then ascending price
        const leadId = TIER_LEAD[usage?.plan_id || ''] || 'pro';
        const sorted = raw
          .slice()
          .sort((a, b) => {
            if (a.id === leadId) return -1;
            if (b.id === leadId) return 1;
            return a.price - b.price;
          })
          .slice(0, 3);
        setPacks(sorted);
      })
      .catch(() => setPacks([]));
  }, [usage, dismissed]);

  // ── render guards ──────────────────────────────────────────────────────
  if (dismissed || !usage) return null;

  const { llm_spent_cents = 0, llm_cap_cents = -1, is_byok = false,
          tokens_monthly = 0, llm_tokens_this_month = 0, plan_id = '' } = usage;

  if (is_byok || llm_cap_cents <= 0) return null;

  const pct = tokens_monthly > 0
    ? Math.min(100, Math.round((llm_tokens_this_month / tokens_monthly) * 100))
    : Math.min(100, Math.round((llm_spent_cents / llm_cap_cents) * 100));

  if (pct < CAP_PCT) return null;

  const allowLabel = tokens_monthly > 0 ? fmtM(tokens_monthly) : `$${(llm_cap_cents / 100).toFixed(2)}`;
  const leadId     = TIER_LEAD[plan_id] || 'pro';

  const handleDismiss = () => { writeDismiss(); setDismissed(true); };

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Monthly token limit reached"
      data-testid="token-cap-gate"
    >
      <div className="relative w-full max-w-xl rounded-2xl border border-white/10 bg-[#0e0d0c] px-6 py-8 shadow-2xl">
        {/* dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-4 rounded-md p-1.5 text-white/30 transition-colors hover:bg-white/10 hover:text-white/70"
          aria-label="Dismiss for now"
          data-testid="cap-gate-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>

        {/* header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/15 ring-1 ring-rose-500/30">
            <Zap className="h-6 w-6 text-rose-500" />
          </div>
          <h2 className="text-[22px] font-bold tracking-tight text-white" data-testid="cap-gate-headline">
            You've reached your {allowLabel} monthly limit
          </h2>
          <p className="mt-1.5 text-[13px] text-white/50">
            Add a token pack to keep writing — packs stack on top of your plan and never expire.
          </p>
        </div>

        {/* upgrade CTA — only for Author / Publisher tiers */}
        {UPGRADE_PATH[plan_id] && (
          <div className="mb-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3"
               data-testid="cap-gate-upgrade-cta">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-white">
                  <ArrowUpCircle className="h-4 w-4 text-sky-400 shrink-0" />
                  Move to {UPGRADE_PATH[plan_id].name} — {UPGRADE_PATH[plan_id].tokens} tokens/mo
                </div>
                <div className="mt-0.5 text-[11px] text-white/40">
                  ${UPGRADE_PATH[plan_id].price}/mo subscription · cancel anytime · tokens reset each month
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleUpgrade(UPGRADE_PATH[plan_id].next_id)}
                disabled={!!buying}
                className="shrink-0 h-8 gap-1.5 border-sky-400/40 text-sky-400 hover:bg-sky-400/10 text-[11.5px]"
                data-testid="cap-gate-upgrade-btn"
              >
                {buying === `upgrade_${UPGRADE_PATH[plan_id].next_id}`
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <><ArrowUpCircle className="h-3.5 w-3.5" /> Upgrade</>}
              </Button>
            </div>
          </div>
        )}

        {/* pack cards */}
        {packs === null && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-white/30" />
          </div>
        )}
        {packs !== null && packs.length === 0 && (
          <div className="flex justify-center py-4">
            <Button
              variant="outline"
              onClick={() => window.open('/pricing', '_blank')}
              className="gap-2"
              data-testid="cap-gate-pricing-link"
            >
              <PackageCheck className="h-4 w-4" /> View token packs →
            </Button>
          </div>
        )}
        {packs !== null && packs.length > 0 && (
          <div className="grid grid-cols-3 gap-3" data-testid="cap-gate-pack-grid">
            {packs.map(pack => (
              <PackCard
                key={pack.id}
                pack={pack}
                buying={buying}
                isLead={pack.id === leadId}
                onBuy={handleBuy}
              />
            ))}
          </div>
        )}

        {/* dismiss link */}
        <p className="mt-5 text-center text-[11.5px] text-white/25">
          <button
            onClick={handleDismiss}
            className="underline underline-offset-2 hover:text-white/50 transition-colors"
            data-testid="cap-gate-dismiss-link"
          >
            Dismiss for now
          </button>
          {' '}— reminder resets in {DISMISS_HOURS} hours
        </p>
      </div>
    </div>
  );
}
